import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Device } from '@ionic-native/device/ngx';
import { DocumentTypes } from '@models/document';
import * as moment from 'moment';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { Environment } from '../../../core/services/common';
import {
  MailMessage,
  MailParticularData,
  SendMessageRequest,
  SendMessageResponse,
  UnreadMails
} from '../../models/mail';
import { Base64File } from '../../models/payment-plan';
import {
  createHttpParams,
  CustomUriEncoder,
  fromEntriesArr,
  parseObjectToKeyValueArray
} from '../../utils/utils';
import { AuthMirrorService } from '../core/auth/auth-mirror.service';
import { BaseService } from '../core/base.service';
import { OperationRegisterService } from '../core/operation-register.service';

@Injectable({
  providedIn: 'root'
})
export class BaseMailService {
  constructor(
    private http: HttpClient,
    private base: BaseService,
    private authMirror: AuthMirrorService,
    private device: Device,
    private or: OperationRegisterService
  ) {}

  sendMessage(request: SendMessageRequest, params?: HttpParams) {
    const url = `${this.base.getPublicBaseUrl()}/mail/sendMessage`;
    return this.http.post<SendMessageResponse>(url, request, {
      ...this.base.getPublicHeaders({ cacheResponse: true }),
      params
    });
  }

  sendContactForm(
    mailboxIdentifier: string,
    particularData: MailParticularData[],
    message: MailMessage
  ): Observable<SendMessageResponse> {
    const msgRequest: SendMessageRequest = {
      mailboxIdentifier,
      particularData,
      message
    };
    return this.sendMessage(msgRequest);
  }

  getOS(platform: string, version: string) {
    if (!platform && !version) {
      if ('platform' in navigator) {
        return navigator.platform;
      }
      else {
        return '';
      }
    }
    return `${platform} ${version}`;
  }

  sendHelpForm({ question, email }: any) {
    const { user } = this.authMirror.authService.getState();
    const { model, platform, version } = this.device;

    const particularData = parseObjectToKeyValueArray({
      Nombre: user.name,
      'Apellido 1': user.surname1,
      'Apellido 2': user.surname2,
      Dispositivo: model || navigator.userAgent,
      'Sistema Operativo': this.getOS(platform, version),
      'Version de la aplicacion': Environment.config().version,
      Comentario: question
    });
    const message: MailMessage = {
      mailBoxId: '3',
      subject: 'Ayuda APP METS',
      read: false,
      inboxId: '1',
      sendingFile: false,
      responseModeId: '3',
      frmId: '590',
      from: email,
      to: email,
      content: question
    };
    const request: SendMessageRequest = {
      mailboxIdentifier: user.documentNumber,
      particularData,
      message
    };

    return this.sendMessage(request).pipe(
      tap(() => this.or.registerHelpAskOK()),
      catchError(error => {
        this.or.registerHelpAskKO({ error, question });
        return throwError(error);
      })
    );
  }

  sendRating(form: any, isPositive: boolean) {
    const { user } = this.authMirror.authService.getState();
    const { model, platform, version } = this.device;
    const starLabel = form.starRating === 1 ? ' estrella' : ' estrellas';

    const particularData = parseObjectToKeyValueArray({
      Nombre: user.name,
      'Apellido 1': user.surname1,
      'Apellido 2': user.surname2,
      NIF: user.documentNumber,
      'Email OIM': user.email,
      Teléfono: user.phone,
      'Version de la aplicacion': Environment.config().version,
      Dispositivo: model || navigator.userAgent,
      'Sistema Operativo': this.getOS(platform, version),
      Valoración: form.starRating + starLabel,
      Comentario:
        form.additionalComments !== '' ? form.additionalComments : 'N/A'
    });
    const message: MailMessage = {
      mailBoxId: '3',
      subject: isPositive
        ? 'Valoración Positiva APP METS'
        : 'Valoración Negativa APP METS',
      read: false,
      inboxId: '1',
      sendingFile: false,
      responseModeId: isPositive ? '7' : '3',
      type: isPositive ? '5' : '3',
      frmId: isPositive ? '591' : '592',
      from: isPositive ? 'Att Cliente' : user.email,
      to: user.email,
      content: isPositive ? 'Observaciones' : ''
    };
    const request: SendMessageRequest = {
      mailboxIdentifier: user.documentNumber,
      particularData,
      message
    };

    const params = this.or.setRatingParams(
      form.starRating,
      form.additionalComments
    );
    return this.sendMessage(request, params);
  }

  sendCopaymentCorresponceStatus(policyNumber: string, content: string) {
    const { user } = this.authMirror.authService.getState();

    const particularData = parseObjectToKeyValueArray({
      Consulta: content
    });

    const message: MailMessage = {
      mailBoxId: '3',
      subject:
        'Confirmación cambio formato envío cartas copago/franquicias ' +
        policyNumber,
      inboxId: '1',
      responseModeId: '6',
      frmId: '584',
      type: '4',
      from: 'Área de Clientes',
      to: '',
      dataId: '',
      read: false,
      sendingFile: false,
      groupId: '74'
    };

    const request: SendMessageRequest = {
      mailboxIdentifier: user.documentNumber,
      particularData,
      message
    };
    return this.sendMessage(request);
  }

  getMessages() {
    const url = `${this.base.getPrivateOIMBaseUrl()}/mailbox`;
    const params = this.or.getMailboxROParams();
    return this.http
      .get<MailMessage[]>(url, {
        ...this.base.getPrivateOIMHeaders(),
        params
      })
      .pipe(
        map(mails =>
          [...mails].sort(
            (a, b) =>
              moment(b.dateTime).valueOf() - moment(a.dateTime).valueOf()
          )
        )
      );
  }

  getMessage(mail: MailMessage) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/mailbox/message`;
    const roParams = this.or.getMailboxROParams(mail);
    const params = this.getMailHttpParams(mail, roParams);
    return this.http.get<MailMessage>(url, {
      ...this.base.getPrivateOIMHeaders(),
      params
    });
  }

  getDocument(mail: MailMessage, fileIndex: number) {
    const { id } = mail.files[fileIndex];
    const url = `${this.base.getPrivateOIMBaseUrl()}/mailbox/message/documents/${id}`;
    const headers = this.base.getPrivateOIMHeaders();
    const roParams = this.or.calculateDocumentROParams(null, {
      type: DocumentTypes.INBOX_DOCUMENT,
      payload: {
        dateTime: mail.dateTime,
        subject: mail.subject
      }
    });
    const params = this.getMailHttpParams(mail, roParams);
    return this.http.get<Base64File>(url, {
      ...headers,
      params
    });
  }

  getUnreadMails() {
    const url = `${this.base.getPrivateOIMBaseUrl()}/mailbox/unread/count`;
    return this.http
      .get<UnreadMails>(url, this.base.getPrivateOIMHeaders())
      .pipe(catchError(() => of({ unreadMails: 0 })));
  }

  private getMailHttpParams(
    { dateTime, type, dataId, frmId }: MailMessage,
    extraParams?: HttpParams
  ) {

    const httpParams = new HttpParams({ encoder: new CustomUriEncoder() });

    const mailParams = {
      dateTime,
      type,
      dataId,
      frmId,
      ...extraParams?.keys()
        .map(param => [param, extraParams.get(param)])
        .reduce(fromEntriesArr, {})
    };

    return createHttpParams({ ...mailParams }, httpParams);
  }
}
